import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import './Goals.css';

const Goals = () => {
  const [goals, setGoals] = useState([]);
  const [challenges, setChallenges] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    target_type: 'courses',
    target_value: 1,
    deadline: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [goalsRes, challengesRes] = await Promise.all([
        api.get('/gamification/goals'),
        api.get('/gamification/challenges'),
      ]);
      setGoals(goalsRes.data);
      setChallenges(challengesRes.data);
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await api.post('/gamification/goals', formData);
      setGoals([response.data, ...goals]);
      setShowForm(false);
      setFormData({
        title: '',
        description: '',
        target_type: 'courses',
        target_value: 1,
        deadline: '',
      });
    } catch (error) {
      console.error('Error creating goal:', error);
    }
  };

  const markComplete = async (goalId) => {
    try {
      const response = await api.put(`/gamification/goals/${goalId}`, {
        completed: true,
      });
      setGoals(goals.map((g) => (g.id === goalId ? response.data : g)));
    } catch (error) {
      console.error('Error updating goal:', error);
    }
  };

  const getTargetTypeLabel = (type) => {
    const labels = {
      courses: 'Complete Courses',
      videos: 'Watch Videos',
      quizzes: 'Pass Quizzes',
      points: 'Earn Points',
      time: 'Study Minutes',
    };
    return labels[type] || type;
  };

  if (loading) {
    return <div className="loading">Loading goals...</div>;
  }

  const activeGoals = goals.filter((g) => !g.completed);
  const completedGoals = goals.filter((g) => g.completed);
  const completedChallenges = challenges.filter(
    (challenge) => challenge.current >= challenge.target
  ).length;

  return (
    <div className="page goals-page">
      <div className="container">
        <section className="goals-hero">
          <div className="hero-content">
            <p className="eyebrow">Planning</p>
            <h1>Goals & Challenges</h1>
            <p>Set targets, monitor daily progress, and celebrate every win.</p>
            <div className="hero-metrics">
              <div>
                <span className="label">Active goals</span>
                <strong>{activeGoals.length}</strong>
              </div>
              <div>
                <span className="label">Completed</span>
                <strong>{completedGoals.length}</strong>
              </div>
              <div>
                <span className="label">Challenges</span>
                <strong>
                  {completedChallenges}/{challenges.length}
                </strong>
              </div>
            </div>
          </div>
          <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
            {showForm ? 'Cancel' : '+ New Goal'}
          </button>
        </section>

        {/* Create Goal Form */}
        {showForm && (
          <div className="goal-form-card">
            <h3>Create New Goal</h3>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Goal Title</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) =>
                    setFormData({ ...formData, title: e.target.value })
                  }
                  placeholder="e.g., Complete 3 courses this month"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) =>
                    setFormData({ ...formData, description: e.target.value })
                  }
                  placeholder="Add more details about your goal..."
                  rows="2"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Target Type</label>
                  <select
                    value={formData.target_type}
                    onChange={(e) =>
                      setFormData({ ...formData, target_type: e.target.value })
                    }
                  >
                    <option value="courses">Complete Courses</option>
                    <option value="videos">Watch Videos</option>
                    <option value="quizzes">Pass Quizzes</option>
                    <option value="points">Earn Points</option>
                    <option value="time">Study Minutes</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Target Value</label>
                  <input
                    type="number"
                    value={formData.target_value}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        target_value: parseInt(e.target.value),
                      })
                    }
                    min="1"
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Deadline</label>
                  <input
                    type="date"
                    value={formData.deadline}
                    onChange={(e) =>
                      setFormData({ ...formData, deadline: e.target.value })
                    }
                  />
                </div>
              </div>

              <button type="submit" className="btn-primary">
                Create Goal
              </button>
            </form>
          </div>
        )}

        <div className="section card-section">
          <div className="section-header">
            <div>
              <p className="label">Momentum</p>
              <h2>Daily Challenges</h2>
            </div>
          </div>
          <div className="challenges-grid">
            {challenges.map((challenge) => {
              const progress = Math.min(
                100,
                (challenge.current / challenge.target) * 100
              );
              const isComplete = challenge.current >= challenge.target;

              return (
                <div
                  key={challenge.id}
                  className={`challenge-card ${isComplete ? 'complete' : ''}`}
                >
                  <div className="challenge-header">
                    <div>
                      <p className="challenge-title">{challenge.title}</p>
                      <span className="challenge-desc">{challenge.description}</span>
                    </div>
                    <span className="challenge-points">+{challenge.points}</span>
                  </div>
                  <div className="challenge-progress">
                    <div className="progress-bar">
                      <div
                        className="progress-fill"
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <span className="challenge-count">
                      {challenge.current}/{challenge.target}
                    </span>
                  </div>
                  {isComplete && <span className="complete-badge">Complete</span>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Active Goals */}
        <div className="section card-section">
          <div className="section-header">
            <div>
              <p className="label">Focus</p>
              <h2>Active Goals ({activeGoals.length})</h2>
            </div>
          </div>
          {activeGoals.length === 0 ? (
            <div className="empty-state">
              <p>No active goals. Create one to start tracking!</p>
            </div>
          ) : (
            <div className="goals-list">
              {activeGoals.map((goal) => {
                const progress = Math.min(
                  100,
                  (goal.current_value / goal.target_value) * 100
                );
                const daysLeft = goal.deadline
                  ? Math.ceil(
                      (new Date(goal.deadline) - new Date()) / (1000 * 60 * 60 * 24)
                    )
                  : null;

                return (
                  <div key={goal.id} className="goal-card">
                    <div className="goal-main">
                      <div className="goal-info">
                        <h4>{goal.title}</h4>
                        {goal.description && <p>{goal.description}</p>}
                        <div className="goal-meta">
                          <span className="goal-type">
                            {getTargetTypeLabel(goal.target_type)}
                          </span>
                          {daysLeft !== null && (
                            <span
                              className={`days-left ${daysLeft < 3 ? 'urgent' : ''}`}
                            >
                              {daysLeft > 0 ? `${daysLeft} days left` : 'Overdue'}
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        className="btn-complete"
                        onClick={() => markComplete(goal.id)}
                      >
                        Mark Complete
                      </button>
                    </div>
                    <div className="goal-progress">
                      <div className="progress-bar">
                        <div
                          className="progress-fill"
                          style={{ width: `${progress}%` }}
                        ></div>
                      </div>
                      <span className="progress-text">
                        {goal.current_value}/{goal.target_value} ({Math.round(progress)}
                        %)
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Completed Goals */}
        {completedGoals.length > 0 && (
          <div className="section card-section">
            <div className="section-header">
              <div>
                <p className="label">Wins</p>
                <h2>Completed Goals ({completedGoals.length})</h2>
              </div>
            </div>
            <div className="goals-list completed">
              {completedGoals.map((goal) => (
                <div key={goal.id} className="goal-card completed">
                  <div className="goal-info">
                    <h4>{goal.title}</h4>
                    <div className="goal-meta">
                      <span className="completed-date">
                        Completed {new Date(goal.completed_at).toLocaleDateString()}
                      </span>
                      {goal.points_awarded && (
                        <span className="points-earned">+50 pts</span>
                      )}
                    </div>
                  </div>
                  <span className="complete-check">✓</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Goals;
